// "Set up your organisation" — the entry point to the admin portal.
//
// Until this screen existed there was NO path to an organisation: `organisation`
// rows could only be created by hand in the Supabase dashboard, so every admin
// sign-in landed on an empty (or fixture-backed) portal. Creation is gated on an
// access code the owner issues, which is why this is a form and not a button.
//
// Tone follows 05_PORTAL_DESIGN.md: warm peach for anything that needs
// attention, never red; generous touch targets; nothing shouts.

import { useState, type FormEvent } from "react";
import type { Locale } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import type { OrganisationType } from "../../domain/types";
import "../../components/portal.css";

const ORG_TYPES: { value: OrganisationType; label: string }[] = [
  { value: "centre", label: "Early childhood centre" },
  { value: "clinic", label: "Therapy clinic" },
  { value: "school", label: "School" },
  { value: "other", label: "Something else" },
];

/**
 * Turns a Postgres SQLSTATE into calm, specific copy.
 *
 * Branching on `code` rather than message text is the contract the RPCs were
 * written to (see the migration header): 42501 not-authorized, P0001 rule
 * violation, P0002 not-found. P0001 covers several distinct rules, so its
 * message is the only place a substring check is justified — and it falls back
 * to a safe generic string rather than leaking raw Postgres text at the user.
 */
export function createOrgErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  const msg = err instanceof Error ? err.message.toLowerCase() : "";

  if (code === "P0002") {
    return "We don't recognise that access code. Please check it and try again.";
  }
  if (code === "P0001") {
    if (msg.includes("expired")) {
      return "That access code has expired. Ask us for a new one and we'll sort it out.";
    }
    if (msg.includes("already used")) {
      return "That access code has already been used. Ask us for a fresh one.";
    }
    if (msg.includes("already belongs")) {
      return "This email already belongs to an organisation. Sign out and use the account you set it up with.";
    }
    if (msg.includes("name is required")) {
      return "Please give your organisation a name.";
    }
    return "We couldn't set that up. Please check the details and try again.";
  }
  if (code === "42501") {
    return "Your session has expired. Please sign in again.";
  }
  return "Something went wrong on our side. Please try again in a moment.";
}

export interface CreateOrganisationProps {
  locale: Locale;
  /** Email of the signed-in adult — shown so they can tell they're on the right account. */
  email?: string;
  /** Called after the organisation exists, so the gate can re-resolve. */
  onCreated: () => void;
  onSignOut: () => void;
}

export function CreateOrganisation({
  email,
  onCreated,
  onSignOut,
}: CreateOrganisationProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<OrganisationType>("centre");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    const trimmedName = name.trim();
    const trimmedCode = code.trim();
    if (!trimmedName) {
      setError("Please give your organisation a name.");
      return;
    }
    if (!trimmedCode) {
      setError("Please enter the access code we sent you.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      await getPortalData().createOrganisation(trimmedName, type, trimmedCode);
      onCreated();
    } catch (err) {
      setError(createOrgErrorCopy(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="zp-root"
      style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden", fontFamily: "var(--font-body)", background: "var(--bg)", color: "var(--ink)" }}
    >
      <div style={{ position: "absolute", top: -160, insetInlineEnd: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--lilac-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />

      <form
        onSubmit={handleSubmit}
        style={{ position: "relative", width: "100%", maxWidth: 470 }}
      >
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--lilac-deep)" }}>
          Organisation portal
        </span>
        <h1 style={{ fontSize: 27, color: "var(--ink-deep)", margin: "10px 0 0", letterSpacing: "-.02em" }}>
          Set up your organisation
        </h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 12, fontSize: 16, lineHeight: 1.65, margin: "12px 0 0" }}>
          This creates your organisation and makes {email ? <b style={{ color: "var(--ink-deep)" }}>{email}</b> : "this account"} its first admin.
        </p>

        <label htmlFor="org-name" style={labelStyle}>Organisation name</label>
        <input
          id="org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Bright Steps Centre"
          autoComplete="organization"
          disabled={busy}
          style={inputStyle}
        />

        <label htmlFor="org-type" style={labelStyle}>What kind of organisation?</label>
        <select
          id="org-type"
          value={type}
          onChange={(e) => setType(e.target.value as OrganisationType)}
          disabled={busy}
          style={inputStyle}
        >
          {ORG_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <label htmlFor="org-code" style={labelStyle}>Access code</label>
        <input
          id="org-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ZOOYLO-PILOT"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={busy}
          aria-describedby="org-code-help"
          style={{ ...inputStyle, letterSpacing: ".06em", fontFamily: "var(--font-mono, ui-monospace, monospace)" }}
        />
        <span id="org-code-help" style={{ display: "block", fontSize: 13.5, color: "var(--ink-faint)", marginTop: 7 }}>
          We send this when your pilot is confirmed. No code yet? Reply to your onboarding email and we'll issue one.
        </span>

        {/* Warm peach, never red — and aria-live so a screen reader hears the
            failure without the focus having to move. */}
        <div role="status" aria-live="polite">
          {error ? (
            <p style={{ marginTop: 18, marginBottom: 0, padding: "12px 14px", background: "var(--peach-tint)", color: "var(--peach-deep)", borderRadius: "var(--r-md)", fontSize: 15, lineHeight: 1.55 }}>
              {error}
            </p>
          ) : null}
        </div>

        <button
          type="submit"
          disabled={busy}
          style={{ marginTop: 22, width: "100%", minHeight: 54, border: "none", background: busy ? "var(--line-strong)" : "var(--brand)", color: "#fff", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 16.5, cursor: busy ? "default" : "pointer" }}
        >
          {busy ? "Setting up…" : "Create organisation"}
        </button>

        <button
          type="button"
          onClick={onSignOut}
          style={{ marginTop: 14, width: "100%", minHeight: 48, border: "none", background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
        >
          Sign in with a different email
        </button>
      </form>
    </div>
  );
}

const labelStyle = {
  display: "block",
  marginTop: 22,
  marginBottom: 8,
  fontSize: 14.5,
  fontWeight: 700,
  color: "var(--ink-deep)",
} as const;

const inputStyle = {
  width: "100%",
  minHeight: 54,
  padding: "0 16px",
  border: "2px solid var(--line-strong)",
  background: "var(--surface)",
  color: "var(--ink)",
  borderRadius: "var(--r-md)",
  fontFamily: "var(--font-body)",
  fontSize: 16.5,
  boxSizing: "border-box",
} as const;
